import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from "classnames";
import styles from "./TeacherModal.module.sass";
import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import axios from "axios";
import { ToastContainer, toast } from 'react-toastify';
import Button from "../../Button";
import { rowProblemLabels } from '../../../utils/teacherWarnings';
const animatedComponents = makeAnimated();

// The problems flagged for one cell (TeacherData::TeacherChecks), rendered
// under it so the fix is typed where the warning is.
const Warnings = ({ warnings, code }) => (
  <>
    {warnings.filter((w) => w.code === code).map((w) => (
      <div
        key={w.code + w.message}
        className={{ notice: styles.notice, alert: styles.alert }[w.level] || styles.warning}
      >
        {w.message}
      </div>
    ))}
  </>
);

// The row's own copy of the summary at the top of the page: each problem that
// would print wrong, by name, under the teacher's name, so a yellow row says
// why at its start. The detail stays under the cell to fix.
const RowProblems = ({ warnings }) => {
  const labels = rowProblemLabels({ warnings });
  if (!labels.length) return null;
  return (
    <ul className={styles.rowProblems}>
      {labels.map((label) => <li key={label}>{label}</li>)}
    </ul>
  );
};
RowProblems.propTypes = {
  warnings: PropTypes.arrayOf(PropTypes.shape({ code: PropTypes.string, level: PropTypes.string })).isRequired,
};

// Rails' CSRF token for the plain Regenerate form (rails-ujs only adds it to
// data-remote requests and Rails-built forms).
const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content || '';

const TeacherItem = ({ teacherList, schoolId, teachers, setTeachers }) => {
  const { register, handleSubmit, control, setValue } = useForm({ defaultValues: { name: teacherList.teacher_name,
      display_name: teacherList.display_name, grade: teacherList.grade, display_grade: teacherList.display_grade } });
  const linked = teacherList.linked_record;
  const warnings = teacherList.warnings || [];
  // The linked record's name in bold red when it does not agree with the
  // display name (the server judges it by the matcher's own rule).
  const linkedInputClass = teacherList.linked_name_matches === false
    ? `${styles.linkedInput} ${styles.linkedInputMismatch}`
    : styles.linkedInput;

  const notify = (message) => {
    toast.success(message, {
      position: 'bottom-right',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  // Swap this teacher's row for the one the server sends back, so the counts,
  // linked record and warnings are what is stored, never what was typed.
  const replaceRow = (row) => {
    if (!row) return; // an empty reply (a refused update) must not blank the row
    setTeachers((prev) => prev.map((teacher) => (teacher.teacher_id === teacherList.teacher_id ? row : teacher)));
  };

  const failed = (message) => () => {
    toast.error(message, { position: 'bottom-right', autoClose: 5000 });
  };

  const onSubmit = (data) => {
    axios
      .put(`/api/v1/educators/${teacherList.teacher_id}`,
        { data:
            {
              type: 'educators',
              id: teacherList.teacher_id,
              attributes: {
                name: data.name,
                display_name: data.display_name,
                grade: data.grade,
                display_grade: data.display_grade,
                student_id: data.student_id?.value,
                school_id: schoolId,
              }
            }
        })
      .then((response) => {
        replaceRow(response.data && response.data.data);
        notify('Teacher successfully updated');
      })
      .catch(failed('The teacher was not saved. Reload the page and try again.'));
  };

  // Rename the linked staff record where it is shown, instead of a round trip
  // to the student screen. The record is saved through the students API, then
  // the educator is re-read so the row's warnings reflect the new name.
  const renameLinkedRecord = (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const firstName = form.elements.first_name.value;
    const lastName = form.elements.last_name.value;
    if (!linked || (firstName === linked.first_name && lastName === linked.last_name)) return;

    axios
      .patch(`/api/v1/students/${linked.id}`, {
        data: { type: 'students', id: String(linked.id), attributes: { first_name: firstName, last_name: lastName } },
      })
      .then(() => axios.put(`/api/v1/educators/${teacherList.teacher_id}`, {
        data: { type: 'educators', id: teacherList.teacher_id, attributes: { school_id: schoolId } },
      }))
      .then((response) => {
        replaceRow(response.data && response.data.data);
        notify('Linked staff record renamed');
      })
      .catch(failed('The linked record was not renamed. Reload the page and try again.'));
  };

  // Duplicate staff rows share a name; what tells them apart is whether the
  // row carries this season's picture (the one the composite can use) and the
  // id the row's warning cites.
  const staffLabel = (student) => {
    let picture = 'no picture';
    if (student.pictured) picture = 'pictured this season';
    else if (student.last_pictured) picture = `last pictured ${student.last_pictured}`;
    const barcode = student.student_id ? ` (${student.student_id})` : '';
    return `${student.name} \u2014 ${picture} \u00b7 #${student.id}${barcode}`;
  };

  const toOption = (student) => ({ value: student.id, label: staffLabel(student), thumbnail: student.thumbnail_url });

  // Every option, flat: Remove, then the staff rows.
  const formatStudents = () => {
    const emptyStudent = { value: '', label: 'Remove' }

    const formattedStudents = teacherList.teachers.map(toOption)

    return (
      [emptyStudent, ...formattedStudents]
    )
  }

  // The dropdown's menu: the likely few (the matcher's candidates for this
  // teacher) above the whole staff list, so three "Mrs. Shepherd" rows are a
  // short list with faces rather than a needle in sixty names.
  const groupedOptions = () => {
    const likelyIds = new Set(teacherList.candidate_ids || []);
    const likely = teacherList.teachers.filter((s) => likelyIds.has(s.id)).map(toOption);
    const rest = teacherList.teachers.filter((s) => !likelyIds.has(s.id)).map(toOption);
    const groups = [{ value: '', label: 'Remove' }];
    if (likely.length) groups.push({ label: `Likely (${likely.length})`, options: likely });
    groups.push({ label: likely.length ? 'All staff' : 'Staff', options: rest });
    return groups;
  };

  // A face beside the name, when the row has this season's picture.
  const optionLabel = (option) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {option.thumbnail && (
        <img
          src={option.thumbnail}
          alt=""
          style={{ height: '44px', width: '34px', objectFit: 'cover', borderRadius: '2px', flex: '0 0 auto' }}
        />
      )}
      <span>{option.label}</span>
    </div>
  );

  const defaultValue = () => {
    const studentRecord =  formatStudents().find(s => s.value === teacherList.student_id);

    return (
      { value: teacherList.student_id, label: studentRecord && studentRecord.label }
    )
  }

  const renderEdit = () => {
    return (
      <>
        <td>
          <form className={styles.form} onBlur={handleSubmit(onSubmit)}>
            <input
              className={styles.input}
              {...register("name")}
            />
          </form>
          <RowProblems warnings={warnings} />
        </td>
        <td>
          {teacherList.student_count}
          <Warnings warnings={warnings} code="small_class" />
        </td>
        {/* Blank before picture day rather than a hard 0, which reads as
            "nobody showed up" when the shoot simply hasn't happened. */}
        <td>
          {teacherList.photographed_count > 0 ? teacherList.photographed_count : '\u2014'}
        </td>
        <td>
          <form className={styles.form} onBlur={handleSubmit(onSubmit)}>
            <input
              className={styles.input}
              {...register("display_name")}
            />
          </form>
          <Warnings warnings={warnings} code="display_name" />
        </td>
        <td>
          <form className={styles.form} onBlur={handleSubmit(onSubmit)}>
            <input
              className={styles.input}
              {...register("grade")}
            />
          </form>
        </td>
        <td>
          <form className={styles.form} onBlur={handleSubmit(onSubmit)}>
            <input
              className={`${styles.input} ${styles.gradeInput}`}
              {...register("display_grade")}
            />
          </form>
          {/* The stored value is what the class picture prints, so it is what
              is shown. When this year's students say otherwise, say so here
              rather than quietly displaying an unsaved value -- Rescan Grades
              writes both fields. */}
          {teacherList.grade_needs_rescan && (
            <div
              className={styles.rescanHint}
              title="This year's students scan as a different grade. Press Rescan Grades to store it."
            >
              students scan as {teacherList.scanned_display_grade || teacherList.grade}
            </div>
          )}
          <Warnings warnings={warnings} code="display_grade" />
        </td>
        <td>
          {/* The staff picture this teacher is linked to: its own name, editable
              here (it is what prints under the teacher's picture), and below it
              the picker to link a different record. */}
          {linked && (
            <>
              <div className={styles.linkedLabel}>Linked record&apos;s name (prints under the picture)</div>
              <form className={styles.linkedName} onBlur={renameLinkedRecord} onSubmit={renameLinkedRecord}>
                <input
                  className={linkedInputClass}
                  name="first_name"
                  defaultValue={linked.first_name}
                  placeholder="First"
                />
                <input
                  className={linkedInputClass}
                  name="last_name"
                  defaultValue={linked.last_name}
                  placeholder="Last"
                />
              </form>
              <div className={styles.linkedLabel}>Link a different staff record</div>
            </>
          )}
          <form className={styles.form} onChange={handleSubmit(onSubmit)}>
            <Controller
              control={control}
              defaultValue={defaultValue()}
              name="student_id"
              render={({ field, value, onChange }) => (
                <Select
                  {...field}
                  onChange={(e) => { setValue('student_id', e); handleSubmit(onSubmit)() }}
                  className="basic-single"
                  components={animatedComponents}
                  defaultValue={defaultValue()}
                  options={groupedOptions()}
                  formatOptionLabel={optionLabel}
                  name="student_id"
                  value={formatStudents().find(s => s.value === value)}
                />)}
            />
          </form>
          <Warnings warnings={warnings} code="linked_record" />
          {/* The class picture, three ways: last year's under this name (the
              quickest way to tell who a roster name is when the picture
              disagrees), this year's as last generated, and a rebuild of this
              year's after corrections -- a plain POST form, so the page that
              opens can wait for the run. All three open in the one DETAIL tab
              beside this list (admin/schools/_composite_tabs). */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '4px', margin: '0 10px 6px' }}>
            <a
              href={`/admin/schools/${schoolId}/class_composite?educator_id=${teacherList.teacher_id}`}
              target="composite-detail"
              className="btn btn-default btn-xs"
              title="Last year's class picture under this teacher's name"
            >
              LYear
            </a>
            <a
              href={`/admin/schools/${schoolId}/class_composite?educator_id=${teacherList.teacher_id}&season=this`}
              target="composite-detail"
              className="btn btn-default btn-xs"
              title="This year's class picture, as last generated"
            >
              Preview
            </a>
            <form
              method="post"
              action={`/admin/schools/${schoolId}/regenerate_class_composite`}
              target="composite-detail"
              style={{ display: 'inline', margin: 0 }}
              onSubmit={confirmRegenerate}
            >
              <input type="hidden" name="authenticity_token" value={csrfToken()} />
              <input type="hidden" name="educator_id" value={teacherList.teacher_id} />
              <button
                type="submit"
                className="btn btn-primary btn-xs"
                title="Rebuild this year's class picture after corrections"
              >
                Regenerate
              </button>
            </form>
          </div>
        </td>
        <br/>
        {/*<Button*/}
        {/*  classes={styles.submit}*/}
        {/*  variant='submit'*/}
        {/*>*/}
        {/*  Save*/}
        {/*</Button>*/}
      </>
    )
  }

  // The plain Regenerate form: a browser confirm, since the rebuild replaces
  // this year's composite for the class.
  const confirmRegenerate = (e) => {
    const question = `Regenerate this year's class picture for "${teacherList.teacher_name}"? `
      + 'It is rebuilt from the current roster and pictures.';
    // eslint-disable-next-line no-alert
    if (!window.confirm(question)) e.preventDefault();
  };

  return (
    <Fragment>

      {/*<a href={`${url}?teacher_id=${teacherList.teacher_id}`}*/}
      {/*   target="_blank"*/}
      {/*   key={teacherList.teacher_id}*/}
      {/*   className={styles.teacherName}>*/}
      {/*  {teacherList.teacher_name || 'No teacher name entered'}*/}
      {/*</a>*/}
      {/*<p className={styles.studentsCount}>Number of students: {teacherList.student_count}</p>*/}

      {renderEdit()}
    </Fragment>
  )
};

export default TeacherItem;
